import { memo, ReactNode, useCallback, useEffect, useRef } from 'react'
import './index.scss'
import { useTranslation } from 'react-i18next'
import Image from '../Image'
import { DateTime } from 'luxon'
import ImageGallery from '../ImageGallery'
import FlotiMap, { LAYER_TYPE } from '../FlotiMap'
import { FlotiMapRef } from '../FlotiMap/type'
import { ICON } from '../Icon/data'
import { COLOR } from '../../data/variables'

export type DisplayData = {
  label: string
  value: string | number | ReactNode
  type?: string
}

type InfoDisplay = {
  label?: string
  data: DisplayData[]
  className?: string
}

const InfoDisplay = memo(({ label, data = [], className = '' }: InfoDisplay) => {
  const mapRef = useRef<FlotiMapRef>(null)
  const { t } = useTranslation()

  const coords = data.find((d) => d.type === 'map')?.value as { lat: number, lon: number, heading: string } | undefined
  const pendingFit = useRef(false)

  useEffect(() => {
    if (mapRef.current && coords) {
      pendingFit.current = true
      mapRef.current.setPoints([{
        lat: coords.lat,
        lon: coords.lon,
        heading: coords.heading,
        icon: ICON.MAP_MARKER,
        id: 1,
        color: COLOR.BLUE_70,
      }])
    }
  }, [mapRef.current, coords])

  const onMapBusy = useCallback((isBusy: boolean) => {
    if (!isBusy && pendingFit.current) {
      pendingFit.current = false
      mapRef.current?.fitInMap(LAYER_TYPE.MARKERS, undefined, 15)
    }
  }, [])

  return (
    <div className={`InfoDisplay ${className}`}>
      {!!label && (
        <div className="InfoDisplay__label">{label}</div>
      )}
      {data.map((d) => {
        const { label: dLabel, value, type } = d
        let formatValue = value ?? t('None')
        if (Array.isArray(formatValue)) formatValue = formatValue.join(', ')
        
        if (type === 'img') {
          if (Array.isArray(value)) {
            return (
              <div className="InfoDisplay__data img">
                <ImageGallery values={value} />
              </div>
            )
          }

          return (
            <div className="InfoDisplay__data img">
              <div className="InfoDisplay__data-label">{dLabel}</div>
              <Image url={value} />
            </div>
          )
        } else if (type === 'datetime') {
          formatValue = DateTime.fromISO(value as string).toLocaleString(DateTime.DATETIME_MED)
        } else if (type === 'map') {
          return (
            <FlotiMap
              ref={mapRef}
              readonly={false}
              isBusyCallback={onMapBusy}
              options={{
                showTiles: false,
                showZoom: false,
              }}
            />
          )
        }

        return (
          <div className="InfoDisplay__data">
            {type !== 'button' && (
              <div className="InfoDisplay__data-label">{dLabel}</div>
            )}
            <div className="InfoDisplay__data-value">{formatValue}</div>
          </div>
        )
      })}
    </div>
  )
})

export default InfoDisplay
